/**
 * TT-850 — pure logic for the Uncategorized Designs review page.
 *
 * TT-846 could only ever dot the first 20 no-category designs on the grid
 * (`selectNoCategoryDesignIds` above); staff had no way to see, or work
 * through, the rest. This page is the full, paginated list with a truthful
 * runtime total from the authoritative backend rather than a hardcoded count,
 * backed by `GET /api/gallery/uncategorized-designs`
 * (`packages/shared/uncategorized_designs.py`), which itself calls the
 * shared TT-847 resolver verbatim.
 *
 * No React here, matching `designQuality.js`'s `runQualityLoad` — the
 * load/page/link/label logic is testable without rendering, and the hook
 * (`hooks/useUncategorizedDesigns.js`) is thin wiring on top of it.
 *
 * Read-only, all the way down: this module never writes a category, never
 * infers one, and never calls a save/like/sync endpoint. The only mutation
 * path it exposes is a plain link back to the existing `/designs` editor.
 */
import { resolveThumbUrl } from "./galleryMerge";

/** Matches the backend's default page size (`DEFAULT_PAGE_LIMIT` in
 * `packages/shared/uncategorized_designs.py`). */
export const UD_PAGE_SIZE = 20;

export const UD_PHASE_IDLE = "idle";
export const UD_PHASE_LOADING = "loading";
export const UD_PHASE_READY = "ready";
export const UD_PHASE_ERROR = "error";

export const UD_ERROR_FALLBACK = "Could not load uncategorized designs.";

export const UD_IDLE_STATE = {
  phase: UD_PHASE_IDLE,
  page: null,
  error: "",
};

/**
 * One page load, as a state machine with no React in it.
 *
 * `isCurrent` is re-read after the await, so an answer for a load the caller
 * has since retired (a page change fired while the previous request was
 * still in flight, or the page being navigated away from) reaches nothing —
 * the same guard `runQualityLoad` uses, for the same reason: the slower of
 * two loads must never win.
 *
 * A failure never yields a page: an empty list and a list that failed to
 * load must never look the same, or staff would read "nothing left to
 * categorize" from a network error.
 */
export async function runUncategorizedDesignsLoad({
  fetchPage,
  isCurrent = () => true,
  onPhase = () => {},
}) {
  if (!isCurrent()) return null;
  onPhase({ phase: UD_PHASE_LOADING, page: null, error: "" });

  let page;
  try {
    page = await fetchPage();
  } catch (ex) {
    if (!isCurrent()) return null;
    const failed = {
      phase: UD_PHASE_ERROR,
      page: null,
      error: (ex && ex.message) || UD_ERROR_FALLBACK,
    };
    onPhase(failed);
    return failed;
  }

  if (!isCurrent()) return null;
  const ready = { phase: UD_PHASE_READY, page, error: "" };
  onPhase(ready);
  return ready;
}

/**
 * The existing design editor is not a URL-routable page (`DesignDetailScreen`
 * opens as an overlay inside `DesignGalleryScreen`'s own component state).
 * This builds a link back to the existing `/designs` route carrying the
 * target id in `?open=`, which `DesignGalleryScreen` reads once on mount to
 * call its own existing `openDetail(id)` — no new editor route is created.
 */
export function designReviewLink(photoId) {
  if (!photoId) return "";
  return `/designs?open=${encodeURIComponent(photoId)}`;
}

/** Reuses the grid's own thumbnail URL resolution — no second image path. */
export function designThumbUrl(item) {
  return resolveThumbUrl(item || {});
}

/**
 * A short, truthful label for a design's publication state — reusing the
 * same gate vocabulary `design_publication.resolve_publication` already
 * returns, never inventing new copy. `publication` is the flattened object
 * the backend returns per item: `{eligible, liked, final, published, reason}`.
 */
export function publicationBadge(publication) {
  const p = publication || {};
  if (!p.eligible) {
    return { tone: "muted", label: "Not eligible for the customer app" };
  }
  if (p.published) {
    return { tone: "ok", label: "Published (no customer category yet)" };
  }
  if (!p.liked) {
    return { tone: "muted", label: "Not liked" };
  }
  if (!p.final) {
    return { tone: "muted", label: "Liked, not marked Final" };
  }
  return { tone: "muted", label: "Not published" };
}
